/**
 * Persistent Toast - 호버 해제 시 사라지는 영구 토스트
 *
 * 네트워크 오류 등 중요한 시스템 알림에 사용.
 * - 토스트가 발생한 시간을 표시
 * - 사용자가 호버한 뒤 마우스를 치우면 자연스럽게 사라짐
 * - 호버 전까지는 계속 남아있음
 *
 * @module core/utils/persistentToast
 */

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, AlertCircle, Info, CheckCircle } from "lucide-react";
import { cn } from "./cn";

type ToastType = "error" | "warning" | "info" | "success";

interface PersistentToastContentProps {
  id: string | number;
  type: ToastType;
  title: string;
  description?: string;
  timestamp: string;
  action?: { label: string; onClick: () => void };
}

const ICON_MAP = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle,
} as const;

const ICON_COLOR = {
  error: "text-error-vivid",
  warning: "text-warning-vivid",
  info: "text-info-vivid",
  success: "text-success-vivid",
} as const;

function PersistentToastContent({
  id,
  type,
  title,
  description,
  timestamp,
  action,
}: PersistentToastContentProps) {
  const [hasHovered, setHasHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setHasHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hasHovered) {
      toast.dismiss(id);
    }
  }, [hasHovered, id]);

  const Icon = ICON_MAP[type];

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "bg-elevated border border-line rounded-lg p-4 shadow-lg w-[356px]",
        "flex gap-3 cursor-default select-none",
        "transition-opacity duration-200",
        hasHovered && "opacity-80"
      )}
    >
      <Icon className={cn("w-5 h-5 mt-0.5 shrink-0", ICON_COLOR[type])} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-main text-sm font-medium">{title}</p>
          <span className="text-muted text-xs shrink-0 tabular-nums">
            {timestamp}
          </span>
        </div>
        {description && (
          <p className="text-muted text-sm mt-1">{description}</p>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="text-sm font-medium text-info-vivid hover:text-info-vivid mt-2"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 영구 토스트 표시 - 호버 후 마우스를 치우면 자연스럽게 사라짐
 *
 * @param type - 토스트 유형 (error, warning, info, success)
 * @param title - 토스트 제목
 * @param options - 설명, 액션 버튼 등
 */
export function persistentToast(
  type: ToastType,
  title: string,
  options?: {
    description?: string;
    action?: { label: string; onClick: () => void };
  }
): string | number {
  const now = new Date();
  const timestamp = now.toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return toast.custom(
    (id) => (
      <PersistentToastContent
        id={id}
        type={type}
        title={title}
        timestamp={timestamp}
        {...(options?.description !== undefined && { description: options.description })}
        {...(options?.action !== undefined && { action: options.action })}
      />
    ),
    { duration: Infinity }
  );
}
